/**
 * Agent Chat Service
 * Hybrid service that uses REST API for conversation management
 * and WebSocket for real-time messaging with RILE Connect agent
 */

import agentApi from './agentApi';
import websocketService from './websocketService';

const AGENT_API_BASE = '/api/conversations'; // Relative to agent base URL

/**
 * Load all conversations from agent
 * @param {boolean} throwOnError - If true, throw errors instead of returning empty array
 * @returns {Promise<Array>} Array of conversation objects
 */
export const loadConversationsFromAgent = async (throwOnError = false) => {
    try {
        const conversations = await agentApi.get(AGENT_API_BASE);

        // Transform to match chat UI format
        return conversations.map((conv) => {
            // Try multiple possible field names for title (API might use different naming)
            const title =
                conv.title ||
                conv.conversation_title ||
                conv.name ||
                (conv.conversation_id === 'default'
                    ? 'Default Conversation'
                    : `Chat ${conv.conversation_id.substring(0, 8)}`);

            // Try multiple possible field names for tags
            const tags = conv.tags || conv.labels || conv.topics || [];

            // Try multiple possible field names for category
            const category = conv.category || conv.type || 'general';

            // Parse last_updated timestamp (null and epoch=0 are
            // common for v2-migrated checkpoints where $checkpoint.ts
            // is inaccessible in binary-serialized data)
            let lastActivity;
            try {
                lastActivity = conv.last_updated
                    ? new Date(conv.last_updated)
                    : null;
                if (
                    !lastActivity ||
                    isNaN(lastActivity.getTime()) ||
                    lastActivity.getTime() === 0
                ) {
                    lastActivity = null;
                }
            } catch {
                lastActivity = null;
            }

            return {
                id: conv.thread_id,
                title,
                lastActivity,
                messageCount: conv.message_count || 0,
                lastMessage: conv.last_message || '',
                category,
                tags: Array.isArray(tags) ? tags : [],
                threadId: conv.thread_id,
                conversationId: conv.conversation_id,
            };
        });
    } catch (error) {
        console.error('Failed to load conversations from agent:', error);
        if (throwOnError) {
            throw error;
        }
        return [];
    }
};

/**
 * Get conversation history from agent
 * @param {string} threadId - Thread ID to retrieve
 * @returns {Promise<Object>} Conversation object with messages
 */
export const getConversationFromAgent = async (threadId) => {
    try {
        const conversation = await agentApi.get(
            `${AGENT_API_BASE}/${threadId}`
        );

        // Safely parse a raw timestamp value into a Date object
        const parseTimestamp = (raw) => {
            if (!raw) return new Date();
            if (raw instanceof Date) return raw;
            const d = new Date(raw);
            return isNaN(d.getTime()) ? new Date() : d;
        };

        // Strip internal metadata prefixes (e.g., [page_context: {...}]) from
        // user messages: these are injected by the widget for agent context but
        // should not be displayed in the UI.
        const stripMetadataPrefix = (text) => {
            return text.replace(/^\[page_context:\s*\{.*\}\]\s*/i, '');
        };

        // Transform messages to match chat UI format
        const messages = (conversation.messages || []).map((msg, index) => {
            // Handle different message format possibilities
            let content = msg.content || msg.text || msg.message || '';
            const role = msg.role || msg.sender || 'unknown';

            // Strip metadata from user messages loaded from the checkpointer
            if (role === 'user' || role === 'human') {
                content = stripMetadataPrefix(content);
            }

            return {
                id: msg.id || `${threadId}-${index}`,
                text: content,
                sender: role === 'user' ? 'user' : 'bot',
                timestamp: parseTimestamp(
                    msg.timestamp || msg.created_at || conversation.last_updated
                ),
            };
        });

        // Try multiple possible field names for title
        const title =
            conversation.title ||
            conversation.conversation_title ||
            conversation.name ||
            `Chat ${conversation.thread_id?.split('_')[1]?.substring(0, 8) || conversation.conversation_id?.substring(0, 8) || 'Default'}`;

        // Try multiple possible field names for tags
        const tags =
            conversation.tags ||
            conversation.labels ||
            conversation.topics ||
            [];

        // Try multiple possible field names for category
        const category =
            conversation.category || conversation.type || 'general';

        // Parse last_updated - use current time as fallback for invalid values
        let lastActivity;
        try {
            lastActivity = new Date(conversation.last_updated);
            if (isNaN(lastActivity.getTime())) {
                lastActivity = new Date();
            }
        } catch {
            lastActivity = new Date();
        }

        return {
            id: threadId,
            title,
            tags: Array.isArray(tags) ? tags : [],
            messages,
            lastActivity,
            metadata: {
                category,
                messageCount: messages.length, // Use actual message count, not the one from API
            },
        };
    } catch (error) {
        console.error('Failed to get conversation from agent:', error);
        throw error;
    }
};

/**
 * Delete a conversation from agent
 * @param {string} threadId - Thread ID to delete
 * @returns {Promise<void>}
 */
export const deleteConversationFromAgent = async (threadId) => {
    try {
        await agentApi.delete(`${AGENT_API_BASE}/${threadId}`);
    } catch (error) {
        console.error('Failed to delete conversation from agent:', error);
        throw error;
    }
};

/**
 * Get usage statistics from agent
 * @returns {Promise<Object>} Usage stats
 */
export const getAgentStats = async () => {
    try {
        const stats = await agentApi.get(`${AGENT_API_BASE}/stats`);
        return {
            totalMessages: stats.total_messages,
            dailyMessages: stats.daily_messages,
            dailyLimit: stats.daily_limit, // Role-based limit from API
            totalChats: stats.total_conversations,
        };
    } catch (error) {
        console.error('Failed to get agent stats:', error);
        return {
            totalMessages: 0,
            dailyMessages: 0,
            dailyLimit: 100, // Default to most restrictive limit
            totalChats: 0,
        };
    }
};

/**
 * Connect to WebSocket for real-time messaging
 * @param {string} threadId - Optional thread ID to connect to
 * @param {string} persona - Persona to connect to ('maya' or 'elena', default: 'maya')
 * @returns {Promise<void>}
 */
export const connectToAgent = async (threadId = null, persona = 'maya') => {
    await websocketService.connect(threadId, persona);
};

/**
 * Get current persona
 * @returns {string} Current persona ('maya' or 'elena')
 */
export const getCurrentPersona = () => {
    return websocketService.getPersona();
};

/**
 * Disconnect from WebSocket
 */
export const disconnectFromAgent = () => {
    websocketService.disconnect();
};

/**
 * Send a message via WebSocket and get response
 * @param {string} message - User message text
 * @param {Object} [metadata] - Optional metadata (e.g., { page_context: 'directory' })
 * @returns {Promise<Object>} Agent response
 */
export const sendMessageToAgent = async (message, metadata) => {
    try {
        const response = await websocketService.sendMessage(message, metadata);

        // Transform response to match chat UI format
        return {
            id: `msg-${Date.now()}`,
            text:
                response.data?.response ||
                response.data?.data?.response ||
                'No response',
            sender: 'bot',
            timestamp: new Date(response.metadata?.time || Date.now()),
            thinking: response.data?.thinking, // Optional thinking process
            title: response.metadata?.title, // Conversation title from LLM
            tags: response.metadata?.tags || [], // Topic tags from LLM
            threadId: response.metadata?.thread_id || null, // Thread ID for conversation tracking
        };
    } catch (error) {
        console.error('Failed to send message to agent:', error);
        throw error;
    }
};

/**
 * Create a new conversation via WebSocket
 * @returns {Promise<Object>} New conversation data
 */
export const createNewAgentConversation = async () => {
    try {
        const result = await websocketService.createNewConversation();
        return {
            threadId: result.thread_id,
            conversationId: result.conversation_id,
        };
    } catch (error) {
        console.error('Failed to create new conversation:', error);
        throw error;
    }
};

/**
 * Switch to a different conversation
 * @param {string} threadId - Thread ID to switch to
 * @returns {Promise<void>}
 */
export const switchAgentConversation = async (threadId) => {
    await websocketService.switchConversation(threadId);
};

/**
 * Register a message handler for incoming WebSocket messages
 * @param {Function} handler - Handler function
 */
export const onAgentMessage = (handler) => {
    websocketService.on('message', handler);
};

/**
 * Unregister a message handler
 * @param {Function} handler - Handler function to remove
 */
export const offAgentMessage = (handler) => {
    websocketService.off('message', handler);
};

/**
 * Check if connected to WebSocket
 * @returns {boolean}
 */
export const isConnectedToAgent = () => {
    return websocketService.isConnected();
};

/**
 * Get current thread ID
 * @returns {string|null}
 */
export const getCurrentThreadId = () => {
    return websocketService.getThreadId();
};

/**
 * Get the last error type from WebSocket connection
 * @returns {string|null} Error type or null
 */
export const getAgentErrorType = () => {
    return websocketService.getLastErrorType();
};

/**
 * Register a close handler for WebSocket disconnection events
 * @param {Function} handler - Handler function
 */
export const onAgentClose = (handler) => {
    websocketService.on('close', handler);
};

/**
 * Unregister a close handler
 * @param {Function} handler - Handler function to remove
 */
export const offAgentClose = (handler) => {
    websocketService.off('close', handler);
};

// Re-export error types for use in components
export { WS_ERROR_TYPES } from './websocketService';
